import React, { useContext, useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { FaMoneyBillWave, FaReceipt } from 'react-icons/fa';
import { FiActivity, FiCreditCard, FiHash, FiPercent, FiShoppingBag } from 'react-icons/fi';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import TopBar from '../components/TopBar';
import layoutStyles from '../styles/layout.module.css';
import styles from './TransactionDetail.module.css';
import { UserContext } from '../App';
import { db } from '../firebase';
import { formatMoney, fromMinorUnits } from '../utils/currency';

function formatDate(value) {
  if (!value?.toDate) return '—';
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(value.toDate());
}

export default function TransactionDetail() {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const appUser = useContext(UserContext);
  const [payment, setPayment] = useState(null);
  const [order, setOrder] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!appUser?.id) return;
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const paymentSnap = await getDoc(doc(db, 'users', appUser.id, 'payments', String(transactionId)));
        const paymentData = paymentSnap.exists() ? { id: paymentSnap.id, ...(paymentSnap.data() || {}) } : null;
        const orderId = String(paymentData?.orderId || transactionId).trim();
        const orderSnap = await getDoc(doc(db, 'orders', orderId));
        const orderData = orderSnap.exists() ? { id: orderSnap.id, ...(orderSnap.data() || {}) } : null;
        const productSnap = orderData?.productId ? await getDoc(doc(db, 'products', String(orderData.productId))) : null;
        if (!active) return;
        setPayment(paymentData);
        setOrder(orderData);
        setProduct(productSnap?.exists() ? { id: productSnap.id, ...(productSnap.data() || {}) } : null);
      } catch (err) {
        if (active) setError(err?.message || 'Unable to load transaction.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [appUser?.id, transactionId]);

  const currency = String(order?.amounts?.currency || payment?.currency || 'USD');
  const subtotalMinor = order?.amounts?.subtotalMinor ?? order?.amounts?.grossMinor ?? payment?.amountSubtotalMinor ?? payment?.amountTotalMinor ?? 0;
  const totalMinor = order?.amounts?.totalMinor ?? payment?.amountTotalMinor ?? order?.amounts?.grossMinor ?? 0;
  const subtotalAmount = fromMinorUnits(Number(subtotalMinor), currency);
  const totalAmount = fromMinorUnits(Number(totalMinor), currency);
  const platformFee = fromMinorUnits(Number(order?.amounts?.platformFeeMinor || 0), currency);
  const artistRoyalty = fromMinorUnits(Number(order?.amounts?.artistRoyaltyMinor || 0), currency);
  const sellerNet = fromMinorUnits(Number(order?.amounts?.sellerNetMinor || 0), currency);
  const isPrimaryPurchase = String(order?.listingId || '').startsWith('primary_');
  const paymentStatus = payment?.paymentStatus || order?.status || 'paid';
  const refundAmount = fromMinorUnits(Number(order?.refunds?.amountRefunded || payment?.amountRefundedMinor || 0), currency);
  const disputeStatus = String(order?.dispute?.status || payment?.disputeStatus || '').trim();
  const settlementStatus = String(order?.settlement?.status || payment?.settlementStatus || '').trim();
  const heldAmount = fromMinorUnits(Number(order?.settlement?.heldAmountMinor || payment?.heldAmountMinor || 0), currency);
  const transferredAmount = fromMinorUnits(Number(order?.settlement?.transferredAmountMinor || payment?.transferredAmountMinor || 0), currency);
  const refundedSettlementAmount = fromMinorUnits(Number(order?.settlement?.refundedAmountMinor || payment?.refundedAmountMinor || 0), currency);
  const disputeAmount = fromMinorUnits(Number(order?.settlement?.disputeAmountMinor || payment?.disputeAmountMinor || 0), currency);
  const recoveryRequired = Boolean(order?.settlement?.recoveryRequired || payment?.recoveryRequired);
  const artistPayoutStatus = String(order?.payouts?.artist?.status || '').trim();
  const sellerPayoutStatus = String(order?.payouts?.seller?.status || '').trim();
  const title = String(product?.title || order?.productSnapshot?.title || payment?.productTitle || order?.productId || transactionId);

  return (
    <div className={layoutStyles.detailPage}>
      <TopBar
        variant="back"
        backLabel="Back"
        onBack={() => navigate(location.state?.fromUnit ? `/units/${location.state.fromUnit}` : (location.state?.backTo || '/transactions'))}
        rightActionLabel="Home"
        onRightAction={() => navigate('/home')}
      />
      <div className={`${layoutStyles.detailMain} ${styles.pageShell}`.trim()}>
        <section className={styles.heroCard}>
          <div>
            <span className={styles.eyebrow}>Transaction Detail</span>
            <h1>{title}</h1>
            <p>Payment details, fee breakdown, and payment references for this drop order.</p>
          </div>
        </section>

        {loading ? <p className={styles.state}>Loading transaction…</p> : null}
        {!loading && error ? <p className={styles.state}>{error}</p> : null}
        {!loading && order ? (
          <section className={styles.grid}>
            <div className={styles.card}><span><FiShoppingBag /> Purchase Type</span><strong>{isPrimaryPurchase ? 'Primary purchase' : 'Resale purchase'}</strong></div>
            <div className={styles.card}><span><FaMoneyBillWave /> Subtotal</span><strong>{formatMoney(subtotalAmount, currency)}</strong></div>
            <div className={styles.card}><span><FaMoneyBillWave /> Fan Total</span><strong>{formatMoney(totalAmount, currency)}</strong></div>
            <div className={styles.card}><span><FiPercent /> Platform Fee</span><strong>{formatMoney(platformFee, currency)}</strong></div>
            <div className={styles.card}><span><FiActivity /> Artist Royalty</span><strong>{formatMoney(artistRoyalty, currency)}</strong></div>
            <div className={styles.card}><span><FaReceipt /> {isPrimaryPurchase ? 'Artist Net' : 'Seller Net'}</span><strong>{formatMoney(sellerNet, currency)}</strong></div>
            <div className={styles.card}><span><FiCreditCard /> Payment Status</span><strong>{paymentStatus}</strong></div>
            <div className={styles.card}><span><FiActivity /> Settlement</span><strong>{settlementStatus || '—'}</strong></div>
            <div className={styles.card}><span><FiHash /> Order ID</span><strong>{order.id}</strong></div>
            <div className={styles.card}><span><FiHash /> Payment Intent</span><strong>{order.payment?.paymentIntentId || '—'}</strong></div>
            <div className={styles.card}><span><FiHash /> Charge</span><strong>{order.payment?.chargeId || '—'}</strong></div>
            <div className={styles.card}><span><FiActivity /> Held on Platform</span><strong>{formatMoney(heldAmount, currency)}</strong></div>
            <div className={styles.card}><span><FiActivity /> Released</span><strong>{formatMoney(transferredAmount, currency)}</strong></div>
            <div className={styles.card}><span><FiActivity /> Refunded in Settlement</span><strong>{refundedSettlementAmount ? formatMoney(refundedSettlementAmount, currency) : '—'}</strong></div>
            <div className={styles.card}><span><FiActivity /> Disputed Amount</span><strong>{disputeAmount ? formatMoney(disputeAmount, currency) : '—'}</strong></div>
            <div className={styles.card}><span><FiActivity /> Artist Payout</span><strong>{artistPayoutStatus || '—'}</strong></div>
            {!isPrimaryPurchase ? <div className={styles.card}><span><FiActivity /> Seller Payout</span><strong>{sellerPayoutStatus || '—'}</strong></div> : null}
            <div className={styles.card}><span><FiActivity /> Recovery</span><strong>{recoveryRequired ? 'Required' : 'Not required'}</strong></div>
            <div className={styles.card}><span><FiHash /> Unit</span><strong>{order.unitIds?.[0] || '—'}</strong></div>
            <div className={styles.card}><span><FiActivity /> Refunded</span><strong>{refundAmount ? formatMoney(refundAmount, currency) : '—'}</strong></div>
            <div className={styles.card}><span><FiActivity /> Dispute</span><strong>{disputeStatus || '—'}</strong></div>
            <div className={styles.card}><span><FiActivity /> Recorded</span><strong>{formatDate(payment?.createdAt || order?.createdAt)}</strong></div>
          </section>
        ) : null}
      </div>
    </div>
  );
}
